import React from "react";

interface LinearGaugeChartProps {
  /** Score value (0-100 for percentage, or actual value if using scale) */
  value: number;
  /** Maximum value (100 for percentage, or scaleMax if using scale) */
  maxValue: number;
  /** Minimum value (0 for percentage, or scaleMin if using scale) */
  minValue?: number;
  /** Hex color code (with #) */
  color: string;
  /** Text to display as the score */
  displayText: string;
}

const LinearGaugeChart: React.FC<LinearGaugeChartProps> = ({
  value,
  maxValue,
  minValue = 0,
  color,
  displayText,
}) => {
  // Calculate percentage for the bar (0-100) accounting for min value
  const range = maxValue - minValue;
  const normalizedValue = value - minValue;
  const percentage = Math.min(
    100,
    Math.max(0, (normalizedValue / range) * 100)
  );

  return (
    <div className="w-full flex flex-col items-center justify-center px-6 py-8">
      {/* Progress Bar with Score Badge */}
      <div className="w-full relative">
        {/* The Bar - Sleeker and thinner */}
        <div className="w-full h-6 bg-gray-100 rounded-full overflow-visible relative flex items-center">
          {/* Filled portion */}
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${percentage}%`,
              backgroundColor: color,
            }}
          />

          {/* Score Badge at the end of the bar - More prominent */}
          <div
            className="absolute top-1/2 -translate-y-1/2 transition-all duration-500 ease-out"
            style={{
              left: `${percentage}%`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div
              className="bg-white rounded-full shadow-lg px-4 py-2 border-[3px] border-current"
              style={{ borderColor: color }}
            >
              <span
                className="text-xl font-extrabold whitespace-nowrap"
                style={{ color }}
              >
                {displayText}
              </span>
            </div>
          </div>
        </div>

        {/* Scale Labels */}
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-400">
            {displayText.includes("%") ? `${minValue}%` : minValue}
          </span>
          <span className="text-xs text-gray-400">
            {maxValue}
            {displayText.includes("%") ? "%" : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LinearGaugeChart;
